import {
  ChangeDetectionStrategy,
  Component,
  Input,
  computed,
  signal
} from '@angular/core';

import { DonutChartBlock } from '@core/services/ai/ai-assistant.service';

const PALETTE = [
  '#6366f1',
  '#8b5cf6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16'
];

@Component({
  selector: 'app-ai-donut-chart-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  template: `
    @if (block.title) {
      <div class="dc__title">{{ block.title }}</div>
    }
    <div class="dc">
      <div class="dc__chart">
        <svg viewBox="0 0 36 36" class="dc__svg">
          @for (s of arcs(); track s.label) {
            <circle
              class="dc__arc"
              cx="18"
              cy="18"
              r="15.9155"
              fill="transparent"
              [attr.stroke]="s.color"
              stroke-width="3.6"
              [attr.stroke-dasharray]="s.dash"
              [attr.stroke-dashoffset]="s.offset"></circle>
          }
        </svg>
        @if (block.centerValue) {
          <div class="dc__center">
            <div class="dc__center-value">{{ block.centerValue }}</div>
            @if (block.centerLabel) {
              <div class="dc__center-label">{{ block.centerLabel }}</div>
            }
          </div>
        }
      </div>
      <ul class="dc__legend">
        @for (s of arcs(); track s.label) {
          <li class="dc__leg-item">
            <span class="dc__leg-dot" [style.background]="s.color"></span>
            <span class="dc__leg-label">{{ s.label }}</span>
            <span class="dc__leg-value">{{ s.percentLabel }}</span>
          </li>
        }
      </ul>
    </div>
    @if (block.caption) {
      <div class="dc__caption">{{ block.caption }}</div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .dc__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 8px;
      }
      .dc {
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        padding: 12px;
        display: flex;
        align-items: center;
        gap: 16px;
      }
      .dc__chart {
        position: relative;
        width: 120px;
        height: 120px;
        flex-shrink: 0;
      }
      .dc__svg {
        width: 100%;
        height: 100%;
        transform: rotate(-90deg);
      }
      .dc__arc {
        transition: stroke-dasharray 0.4s ease;
      }
      .dc__center {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }
      .dc__center-value {
        font-weight: 700;
        font-size: 16px;
        color: rgba(15, 23, 42, 0.92);
      }
      .dc__center-label {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
      }
      .dc__legend {
        list-style: none;
        margin: 0;
        padding: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
      }
      .dc__leg-item {
        display: grid;
        grid-template-columns: 12px 1fr auto;
        gap: 8px;
        align-items: center;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.85);
      }
      .dc__leg-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
      }
      .dc__leg-label {
        overflow-wrap: anywhere;
      }
      .dc__leg-value {
        font-weight: 600;
        color: rgba(15, 23, 42, 0.7);
        white-space: nowrap;
      }
      .dc__caption {
        margin-top: 8px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.55);
        font-style: italic;
      }
    `
  ]
})
export class AiDonutChartBlockComponent {
  private readonly _block = signal<DonutChartBlock | null>(null);

  @Input({ required: true })
  set block(value: DonutChartBlock) {
    this._block.set(value);
  }
  get block(): DonutChartBlock {
    return this._block()!;
  }

  readonly arcs = computed<
    {
      label: string;
      color: string;
      dash: string;
      offset: number;
      percentLabel: string;
    }[]
  >(() => {
    const b = this._block();
    if (!b) return [];
    // Defensive: a malformed block (missing `slices`) degrades to an
    // empty donut instead of crashing the message render.
    const slices = Array.isArray(b.slices) ? b.slices : [];
    const total = slices.reduce((acc, s) => acc + Math.max(0, s.value), 0);
    if (total <= 0) return [];
    let cumulative = 0;
    const circumference = 100;
    return slices.map((s, idx) => {
      const pct = Math.max(0, s.value) / total;
      const length = pct * circumference;
      const offset = -cumulative;
      cumulative += length;
      return {
        label: s.label,
        color: s.color || PALETTE[idx % PALETTE.length],
        dash: `${length} ${circumference - length}`,
        offset,
        percentLabel: `${Math.round(pct * 100)}%`
      };
    });
  });
}
